import { useState, useEffect } from 'react'
import { fetchWeather } from '../../../lib/weather'

export function WeatherPanel({ settings }) {
  const [weather, setWeather] = useState(null)
  const [error,   setError]   = useState(null)

  const { weather_latitude: lat, weather_longitude: lon, weather_unit: unit = 'fahrenheit', weather_location_label: locationLabel } = settings ?? {}
  const unitSymbol = unit === 'celsius' ? '°C' : '°F'

  useEffect(() => {
    if (!lat || !lon) return
    let cancelled = false

    async function load() {
      try {
        const w = await fetchWeather(lat, lon, unit)
        if (!cancelled) setWeather(w)
      } catch (_) {
        if (!cancelled) setError('Unable to load weather')
      }
    }

    load()
    const interval = setInterval(load, 60 * 1000)
    return () => { cancelled = true; clearInterval(interval) }
  }, [lat, lon, unit])

  if (!lat || !lon) {
    return (
      <div className="flex flex-col h-full items-center justify-center panel-animate-in px-4">
        <div className="text-5xl sm:text-6xl mb-3">🌡️</div>
        <p className="text-gray-400 text-lg sm:text-xl text-center">Weather not configured</p>
        <p className="text-gray-600 text-sm mt-1 text-center">Add your location in Settings → Wall Display</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col h-full items-center justify-center panel-animate-in">
        <p className="text-gray-500 text-lg">{error}</p>
      </div>
    )
  }

  if (!weather) {
    return (
      <div className="flex flex-col h-full items-center justify-center panel-animate-in">
        <div className="text-3xl animate-pulse">🌤️</div>
      </div>
    )
  }

  const { current, forecast } = weather
  const [, ...forecastDays] = forecast

  return (
    <div className="flex flex-col h-full px-4 sm:px-8 py-4 sm:py-6 panel-animate-in">
      <div className="flex items-baseline justify-between mb-2">
        <p className="text-gray-500 text-xs sm:text-sm font-semibold uppercase tracking-widest">Weather</p>
        {locationLabel && <p className="text-gray-500 text-xs sm:text-sm">{locationLabel}</p>}
      </div>

      {/* Current */}
      <div className="flex-1 flex flex-col items-center justify-center gap-1">
        <div className="text-5xl sm:text-7xl lg:text-8xl">{current.icon}</div>
        <div className="text-5xl sm:text-7xl lg:text-8xl font-bold text-white leading-none tabular-nums">
          {current.temperature}{unitSymbol}
        </div>
        <p className="text-gray-300 text-xl sm:text-3xl mt-1">{current.condition}</p>
        <p className="text-gray-500 text-sm sm:text-lg mt-1">
          Feels like {current.feelsLike}{unitSymbol} · Wind {current.windSpeed} mph
        </p>
        <p className="text-gray-400 text-base sm:text-xl mt-2">
          H: {forecast[0]?.high}{unitSymbol} · L: {forecast[0]?.low}{unitSymbol}
        </p>
      </div>

      {/* 3-day forecast */}
      <div className="flex gap-3 sm:gap-4 pt-4 border-t border-white/10 flex-shrink-0">
        {forecastDays.slice(0, 3).map((day) => (
          <div key={day.date} className="flex-1 text-center">
            <p className="text-gray-400 text-xs sm:text-sm font-semibold">{day.dayName}</p>
            <p className="text-xl sm:text-2xl my-1">{day.icon}</p>
            <p className="text-white text-xs sm:text-sm font-bold">{day.high}°</p>
            <p className="text-gray-500 text-xs sm:text-sm">{day.low}°</p>
            {day.precipChance > 20 && (
              <p className="text-sky-400 text-xs mt-0.5">{day.precipChance}%</p>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
